import { get, isFunction, isUndefined } from 'lodash-es';

import { XYAxisTypeTime } from '../../../types/axis.js';
import { isCategorical, isTime } from '../../../types/guards.js';
import { ScaleTypes, ScaleTypesMap } from '../../../types/scale-types-map.js';
import {
  type RectDatapointInternal,
  type XYChartAreaSeriesInternal,
  type XYChartAreaVariant,
  XYChartAxisInternal,
  type XYChartOrientation,
  type XYChartBarSeriesInternal,
  type XYChartDotSeriesInternal,
  type XYChartLineSeriesInternal,
  type XYChartRectSeriesInternal,
  type XYValueAccessorDataTypes,
} from '../../../types/xy-chart-internals.js';
import {
  DotDatapoint,
  type LineDatapoint,
  type XYAccessorDataTypes,
  type XYChartData,
} from '../../../types/xy-chart.js';
import { castDataType } from '../cast-data/cast-data-types.js';

export function getDatumValue(
  datum: XYChartData,
  path: string | string[] | ((data: XYChartData) => string) | undefined,
): XYAccessorDataTypes | undefined {
  if (!path) {
    return undefined;
  }

  if (isFunction(path)) {
    return path(datum);
  }

  return get(datum, path);
}

export function getPoint<T extends ScaleTypes>(
  datum: XYChartData,
  path: string | undefined,
  axisType: T,
): ScaleTypesMap[T]['castDataType'] {
  const value = getDatumValue(datum, path);

  return castDataType(value, axisType);
}

/**
 * Generic function to generate a data point from the provided datum and series properties.
 * Used internally by getDotDatapoint and getLineDatapoint.
 *
 * @param datum - The individual data point.
 * @param seriesProps - The series path properties.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The data point, or undefined if any required value is missing or invalid.
 */
export function getBaseDatapoint<T extends DotDatapoint | LineDatapoint>(
  datum: XYChartData,
  seriesProps: XYChartDotSeriesInternal | XYChartLineSeriesInternal,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): T | undefined {
  const x0 = getPoint(datum, seriesProps.x0Accessor, xAxis.type);
  const y0 = getPoint(datum, seriesProps.y0Accessor, yAxis.type);
  let x1;
  let y1;

  if (isUndefined(x0) || isUndefined(y0)) {
    return;
  }

  if (isTime(xAxis.type) && seriesProps.x1Accessor) {
    x1 = getPoint(datum, seriesProps.x1Accessor, XYAxisTypeTime);
  }

  if (isTime(yAxis.type) && seriesProps.y1Accessor) {
    y1 = getPoint(datum, seriesProps.y1Accessor, XYAxisTypeTime);
  }

  return { ...datum, x0, x1, y0, y1 } as T;
}

/**
 * Generates a dot data point from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param seriesProps - The series path properties for the dot.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The dot series data point, or undefined if any required value is missing or invalid.
 */
export function getDotDatapoint(
  datum: XYChartData,
  seriesProps: XYChartDotSeriesInternal,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): DotDatapoint | undefined {
  return getBaseDatapoint<DotDatapoint>(datum, seriesProps, xAxis, yAxis);
}

/**
 * Generates a rect data point from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param seriesProps - The series paths properties for the rect.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The rect data point, or undefined if any required value is missing or invalid.
 */
export function getRectDatapoint(
  datum: XYChartData,
  seriesProps: XYChartRectSeriesInternal,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): RectDatapointInternal | undefined {
  const x0 = getPoint(datum, seriesProps.x0Accessor, xAxis.type);

  if (x0 === undefined) {
    return undefined;
  }

  const x1 =
    seriesProps.x1Accessor &&
    getPoint(datum, seriesProps.x1Accessor, xAxis.type);

  if (x1 === undefined && !isCategorical(xAxis.type)) {
    return undefined;
  }

  const y0 = getPoint(datum, seriesProps.y0Accessor, yAxis.type);

  if (y0 === undefined) {
    return undefined;
  }

  const y1 =
    seriesProps.y1Accessor &&
    getPoint(datum, seriesProps.y1Accessor, yAxis.type);

  if (y1 === undefined && !isCategorical(yAxis.type)) {
    return undefined;
  }

  const value = getDatumValue(
    datum,
    seriesProps.valueAccessor,
  ) as XYValueAccessorDataTypes;

  return {
    ...datum,
    value,
    x0,
    x1,
    y0,
    y1,
  };
}

/**
 * Generates a line data point from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param seriesProps - The series path properties for the line.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The line series data point, or undefined if any required value is missing or invalid.
 */
export function getLineDatapoint(
  datum: XYChartData,
  seriesProps: XYChartLineSeriesInternal,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): LineDatapoint | undefined {
  return getBaseDatapoint<LineDatapoint>(datum, seriesProps, xAxis, yAxis);
}

export const getAxisType = (
  axisName: 'x' | 'y',
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
) => {
  const axisType = {
    x: xAxis,
    y: yAxis,
  };

  return axisType[axisName].type;
};

export const getChartOrientation = ({
  x1Accessor,
  x0Accessor,
  y1Accessor,
  y0Accessor,
}: XYChartBarSeriesInternal | XYChartAreaSeriesInternal):
  | XYChartOrientation
  | undefined => {
  const isVertical = !!(x0Accessor && y1Accessor);
  const isHorizontal = !!(x1Accessor && y0Accessor);
  const isFullSquare = isVertical && isHorizontal;

  if (isFullSquare) {
    return 'both';
  }
  if (isVertical) {
    return 'vertical';
  }
  if (isHorizontal) {
    return 'horizontal';
  } else {
    return undefined;
  }
};

export const getChartAreaVariant = (
  chartOrientation: XYChartOrientation,
  seriesProps: XYChartAreaSeriesInternal,
): XYChartAreaVariant => {
  if (chartOrientation === 'both') {
    return 'band';
  }
  if (chartOrientation === 'horizontal') {
    return seriesProps.x0Accessor ? 'band' : 'area';
  }

  return seriesProps.y0Accessor ? 'band' : 'area';
};
